import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import { logMessage } from "./amplifyLogger";
import { clientLog } from "../services/logging";
import { MIN_PHONE_LENGTH } from "../constants";

type ParsedPhoneNumber = ReturnType<typeof parsePhoneNumber>;

interface PhoneNumberInfo {
  country?: string | null;
  phoneNumber?: string | null;
  isValid?: boolean;
  formattedPhoneNumber?: string | null;
  errorMessage?: boolean | null;
}

export const isPhoneNumber = (uiValue: string): boolean => {
  try {
    return isValidPhoneNumber(uiValue);
  } catch {
    return false;
  }
};

export const phoneNumberDetail = (phoneNumber: string): PhoneNumberInfo => {
  let phoneNumberInfo: PhoneNumberInfo = {};
  try {
    const validationResult = isPhoneNumber(phoneNumber);
    if (validationResult) {
      const parsedPhoneNumber = parsePhoneNumber(phoneNumber);
      phoneNumberInfo.country = parsedPhoneNumber.country ?? null;
      phoneNumberInfo.phoneNumber =
        parsedPhoneNumber.country === "US" || parsedPhoneNumber.country === "CA"
          ? parsedPhoneNumber.nationalNumber
          : parsedPhoneNumber.number;
      phoneNumberInfo.isValid = parsedPhoneNumber.isValid();
      phoneNumberInfo.formattedPhoneNumber =
        formatPhoneNumber(parsedPhoneNumber) ?? null;
      phoneNumberInfo.errorMessage = null;
    } else {
      phoneNumberInfo.country = null;
      phoneNumberInfo.phoneNumber = null;
      phoneNumberInfo.isValid = false;
      phoneNumberInfo.formattedPhoneNumber = null;
      phoneNumberInfo.errorMessage = validationResult;
    }
  } catch (error) {
    clientLog({
      component: `phonenumber`,
      message: `Something went wrong in phoneNumberDetail => ${error}`,
    });
    logMessage(
      "phoneNumber",
      `Unable to get phoneNumber detail from method phoneNumberDetail => ${error}`,
      "error",
    );
  }

  return phoneNumberInfo;
};

const formatPhoneNumber = (
  parsedPhoneNumber: ParsedPhoneNumber,
): string | undefined => {
  let formattedPhoneNumber: string | undefined;

  try {
    formattedPhoneNumber = parsedPhoneNumber.number;
  } catch (err) {
    clientLog({
      component: `phonenumber`,
      message: `Something went wrong in format PhoneNumber. ${err}`,
    });
    logMessage(
      "phoneNumber",
      `Unable to format phonenumber from method formatPhoneNumber. Error: ${err}`,
      "error",
    );
  }

  return formattedPhoneNumber;
};

/**
 * Simple phone number length validation
 * @param value - Phone number string to validate
 * @param required - If true, empty values are invalid; if false, empty values are valid
 * @returns true if valid, false if invalid
 */
export const isPhoneValid = (value: string, required = false): boolean => {
  const len = value.trim().length;
  if (len === 0) return !required;
  return len >= MIN_PHONE_LENGTH;
};

/**
 * Sanitize country code input - allows only "+" at start followed by up to 4 digits
 * @param value - Raw input value
 * @returns Sanitized country code string
 */
export const sanitizeCountryCode = (value: string): string => {
  const sanitized = value.replace(/[^\d+]/g, "");
  return sanitized.startsWith("+")
    ? "+" + sanitized.slice(1).replace(/\+/g, "").slice(0, 4)
    : sanitized.replace(/\+/g, "").slice(0, 4);
};

/**
 * Sanitize phone number input - allows only digits
 * @param value - Raw input value
 * @returns Sanitized phone number string (digits only)
 */
export const sanitizePhoneNumber = (value: string): string => {
  return value.replace(/\D/g, "");
};
